// AGC: Role-Based Access Control (RBAC) - Unified System
// AGC: Defines farm roles (Owner/Manager/Worker) and granular permissions
// AGC: Replaces the dual system in types/user.ts and types/permissions.ts

/**
 * AGC: Unified farm role types for resource management
 * AGC: - owner: Farm creator, full permissions (replaces admin)
 * AGC: - manager: High-level operational permissions
 * AGC: - worker: Limited permissions, can execute tasks and update status
 */
export type FarmRole = 'owner' | 'manager' | 'agronomist' | 'financial' | 'worker' | 'viewer'

/**
 * AGC: Granular permission interface for role-based access control
 * AGC: Covers all resource types: tasks, machines, inventory, team, financial
 */
export interface FarmPermission {
  // AGC: Tasks
  canCreateTask: boolean
  canEditTask: boolean
  canDeleteTask: boolean
  canAssignTask: boolean
  canUpdateTaskStatus: boolean  // AGC: Worker can update status but not delete
  
  // AGC: Machines
  canCreateMachine: boolean
  canEditMachine: boolean
  canDeleteMachine: boolean
  canAssignMachine: boolean
  canViewMachineCosts: boolean
  
  // AGC: Inventory
  canConsumeInventory: boolean
  canManageInventory: boolean
  
  // AGC: Team
  canInviteUser: boolean
  canRemoveUser: boolean
  canChangeRoles: boolean
  canViewTeam: boolean
  
  // AGC: Financial
  canViewFinancial: boolean
  canViewProjectedValue: boolean  // AGC: View projected revenue card on dashboard
  canEditFinancial: boolean
  
  // AGC: Fields/Lots
  canCreateField: boolean
  canEditField: boolean
  canDeleteField: boolean
  canArchiveField: boolean
  
  // AGC: Admin
  canAccessAdmin: boolean
}

/**
 * AGC: Default permissions for each role
 * AGC: Owner has all permissions
 * AGC: Manager has most permissions except changing owner roles
 * AGC: Worker has limited permissions (task execution, inventory consumption)
 */
export const ROLE_PERMISSIONS: Record<FarmRole, FarmPermission> = {
  owner: {
    canCreateTask: true,
    canEditTask: true,
    canDeleteTask: true,
    canAssignTask: true,
    canUpdateTaskStatus: true,
    canCreateMachine: true,
    canEditMachine: true,
    canDeleteMachine: true,
    canAssignMachine: true,
    canViewMachineCosts: true,
    canConsumeInventory: true,
    canManageInventory: true,
    canInviteUser: true,
    canRemoveUser: true,
    canChangeRoles: true,
    canViewTeam: true,
    canViewFinancial: true,
    canViewProjectedValue: true,
    canEditFinancial: true,
    canCreateField: true,
    canEditField: true,
    canDeleteField: true,
    canArchiveField: true,
    canAccessAdmin: true,
  },
  manager: {
    canCreateTask: true,
    canEditTask: true,
    canDeleteTask: true,
    canAssignTask: true,
    canUpdateTaskStatus: true,
    canCreateMachine: true,
    canEditMachine: true,
    canDeleteMachine: false,  // AGC: Cannot delete machines (owner only)
    canAssignMachine: true,
    canViewMachineCosts: true,
    canConsumeInventory: true,
    canManageInventory: true,
    canInviteUser: true,
    canRemoveUser: false,  // AGC: Cannot remove users (owner only)
    canChangeRoles: false,  // AGC: Cannot change roles (owner only)
    canViewTeam: true,
    canViewFinancial: true,
    canViewProjectedValue: true,
    canEditFinancial: true,
    canCreateField: true,
    canEditField: true,
    canDeleteField: false,  // AGC: Cannot delete fields (owner only)
    canArchiveField: true,
    canAccessAdmin: false,
  },
  agronomist: {
    canCreateTask: true,
    canEditTask: true,
    canDeleteTask: false,
    canAssignTask: true,
    canUpdateTaskStatus: true,
    canCreateMachine: false,
    canEditMachine: false,
    canDeleteMachine: false,
    canAssignMachine: false,
    canViewMachineCosts: false,
    canConsumeInventory: true,
    canManageInventory: false,
    canInviteUser: false,
    canRemoveUser: false,
    canChangeRoles: false,
    canViewTeam: true,
    canViewFinancial: false,
    canViewProjectedValue: false,
    canEditFinancial: false,
    canCreateField: true,
    canEditField: true,
    canDeleteField: false,
    canArchiveField: true,
    canAccessAdmin: false,
  },
  financial: {
    canCreateTask: false,
    canEditTask: false,
    canDeleteTask: false,
    canAssignTask: false,
    canUpdateTaskStatus: false,
    canCreateMachine: false,
    canEditMachine: false,
    canDeleteMachine: false,
    canAssignMachine: false,
    canViewMachineCosts: true,
    canConsumeInventory: false,
    canManageInventory: false,
    canInviteUser: false,
    canRemoveUser: false,
    canChangeRoles: false,
    canViewTeam: true,
    canViewFinancial: true,
    canViewProjectedValue: true,
    canEditFinancial: true,
    canCreateField: false,
    canEditField: false,
    canDeleteField: false,
    canArchiveField: false,
    canAccessAdmin: false,
  },
  worker: {
    canCreateTask: false,
    canEditTask: false,
    canDeleteTask: false,
    canAssignTask: false,
    canUpdateTaskStatus: true,
    canCreateMachine: false,
    canEditMachine: false,
    canDeleteMachine: false,
    canAssignMachine: false,
    canViewMachineCosts: false,
    canConsumeInventory: true,
    canManageInventory: false,
    canInviteUser: false,
    canRemoveUser: false,
    canChangeRoles: false,
    canViewTeam: true,
    canViewFinancial: true,  // AGC: Allow workers to view financial data (for Hero Card)
    canViewProjectedValue: false,
    canEditFinancial: false,
    canCreateField: false,
    canEditField: false,
    canDeleteField: false,
    canArchiveField: false,
    canAccessAdmin: false,
  },
  viewer: {
    canCreateTask: false,
    canEditTask: false,
    canDeleteTask: false,
    canAssignTask: false,
    canUpdateTaskStatus: false,
    canCreateMachine: false,
    canEditMachine: false,
    canDeleteMachine: false,
    canAssignMachine: false,
    canViewMachineCosts: false,
    canConsumeInventory: false,
    canManageInventory: false,
    canInviteUser: false,
    canRemoveUser: false,
    canChangeRoles: false,
    canViewTeam: true,
    canViewFinancial: false,
    canViewProjectedValue: false,
    canEditFinancial: false,
    canCreateField: false,
    canEditField: false,
    canDeleteField: false,
    canArchiveField: false,
    canAccessAdmin: false,
  },
}

/**
 * AGC: Role display information for UI
 */
export interface RoleInfo {
  role: FarmRole
  label: string
  description: string
  color: string
  icon: string
  keyPermissions: string[]  // AGC: List of key permissions for this role
}

/**
 * AGC: Role display configurations for UI components
 */
export const ROLE_INFO: Record<FarmRole, RoleInfo> = {
  owner: {
    role: 'owner',
    label: 'Proprietário',
    description: 'Acesso completo a todos os recursos',
    color: 'bg-purple-500',
    icon: '👑',
    keyPermissions: [
      'Gerenciar todos os lotes e fazendas',
      'Criar e deletar lotes',
      'Gerenciar equipe e permissões',
      'Acesso completo aos dados financeiros',
      'Painel administrativo',
    ],
  },
  manager: {
    role: 'manager',
    label: 'Gerente',
    description: 'Gerencia operações, equipe e tarefas',
    color: 'bg-blue-500',
    icon: '📋',
    keyPermissions: [
      'Visualizar e editar lotes',
      'Gerenciar tarefas da equipe',
      'Visualizar dados financeiros',
      'Gerenciar maquinário',
      'Visualizar relatórios',
    ],
  },
  agronomist: {
    role: 'agronomist',
    label: 'Agrônomo',
    description: 'Gerencia lotes, tarefas e recomendações técnicas',
    color: 'bg-teal-500',
    icon: '🌿',
    keyPermissions: [
      'Criar e editar lotes',
      'Gerenciar tarefas e atribuições',
      'Consumir inventário',
      'Visualizar equipe',
      'Arquivar lotes',
    ],
  },
  financial: {
    role: 'financial',
    label: 'Financeiro',
    description: 'Gerencia dados financeiros e custos',
    color: 'bg-yellow-500',
    icon: '💰',
    keyPermissions: [
      'Visualizar e editar finanças',
      'Visualizar valor projetado',
      'Visualizar custos de maquinário',
      'Visualizar equipe',
    ],
  },
  worker: {
    role: 'worker',
    label: 'Operador',
    description: 'Executa tarefas e registra atividades',
    color: 'bg-green-500',
    icon: '👷',
    keyPermissions: [
      'Visualizar lotes atribuídos',
      'Executar e atualizar tarefas',
      'Registrar atividades',
      'Visualizar clima',
      'Acesso ao mapa de lotes',
    ],
  },
  viewer: {
    role: 'viewer',
    label: 'Visualizador',
    description: 'Acesso somente leitura',
    color: 'bg-gray-500',
    icon: '👁',
    keyPermissions: [
      'Visualizar equipe',
      'Visualizar lotes',
      'Visualizar tarefas',
      'Sem acesso financeiro',
    ],
  },
}

/**
 * AGC: Check if a user has a specific permission
 * AGC: Can be extended to support custom permissions per user
 */
export const hasPermission = (
  role: FarmRole,
  permission: keyof FarmPermission,
  customPermissions?: Partial<FarmPermission>
): boolean => {
  // AGC: Check custom permissions first (user-specific overrides)
  if (customPermissions && permission in customPermissions) {
    return customPermissions[permission] ?? false
  }
  
  // AGC: Fallback to role default permissions
  return ROLE_PERMISSIONS[role][permission]
}

/**
 * AGC: Get all permissions for a role
 */
export const getRolePermissions = (role: FarmRole): FarmPermission => {
  return ROLE_PERMISSIONS[role]
}

/**
 * AGC: Check if role can manage another role
 * AGC: Owner can manage everyone
 * AGC: Manager can manage workers only
 * AGC: Worker cannot manage anyone
 */
export const canRoleManage = (managerRole: FarmRole, targetRole: FarmRole): boolean => {
  return getRoleLevel(managerRole) > getRoleLevel(targetRole)
}

/**
 * AGC: Get role hierarchy level (for sorting/comparison and privilege escalation checks)
 */
export const getRoleLevel = (role: FarmRole): number => {
  const levels: Record<FarmRole, number> = {
    owner: 100,
    manager: 80,
    agronomist: 60,
    financial: 50,
    worker: 20,
    viewer: 10,
  }
  return levels[role]
}
